import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useScrollLock } from '../../hooks/useScrollLock';
import { cn } from '../../lib/utils';
import {
  MODAL_LAYOUT_SPRING,
  MODAL_OVERLAY_FADE,
  MODAL_PANEL_ENTER,
  MODAL_SIZE_TRANSITION,
} from '../../lib/motionPresets';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '5xl' | '6xl';

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
};

type ModalShellProps = {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  size?: ModalSize;
  /** Animate explicit max-width in px (overrides size class width cap). */
  animateWidth?: number;
  zIndex?: number;
  className?: string;
  panelClassName?: string;
  /** Accessible title id for aria-labelledby */
  ariaLabelledBy?: string;
  /** When false, backdrop click does not close. Default true. */
  closeOnBackdrop?: boolean;
  /** When true, skip panel scale/spring and layout animations. */
  staticPanel?: boolean;
};

export function ModalShell({
  open,
  onClose,
  children,
  size = '2xl',
  animateWidth,
  zIndex = 100,
  className,
  panelClassName,
  ariaLabelledBy,
  closeOnBackdrop = true,
  staticPanel = false,
}: ModalShellProps) {
  const reduceMotion = useReducedMotion();
  useScrollLock(open);

  const useStaticPanel = reduceMotion || staticPanel;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (useStaticPanel) {
    if (!open) return null;
    return createPortal(
      <div
        className={cn(
          'fixed inset-0 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm',
          className,
        )}
        style={{ zIndex }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={ariaLabelledBy}
          className={cn(
            'relative z-10 flex w-full max-h-[90vh] flex-col overflow-hidden rounded-[32px] bg-white shadow-2xl',
            animateWidth == null && SIZE_CLASSES[size],
            panelClassName,
          )}
          style={animateWidth != null ? { maxWidth: animateWidth } : undefined}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <AnimatePresence>
      {open && (
        <div
          className={cn(
            'fixed inset-0 flex items-center justify-center p-4',
            className,
          )}
          style={{ zIndex }}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={MODAL_OVERLAY_FADE}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeOnBackdrop ? onClose : undefined}
            aria-hidden
          />
          <motion.div
            layout
            layoutRoot
            role="dialog"
            aria-modal="true"
            aria-labelledby={ariaLabelledBy}
            initial={MODAL_PANEL_ENTER.initial}
            animate={{
              ...MODAL_PANEL_ENTER.animate,
              ...(animateWidth != null ? { maxWidth: animateWidth } : {}),
            }}
            exit={MODAL_PANEL_ENTER.exit}
            transition={{
              ...MODAL_PANEL_ENTER.transition,
              layout: MODAL_LAYOUT_SPRING,
              ...(animateWidth != null ? { maxWidth: MODAL_SIZE_TRANSITION } : {}),
            }}
            className={cn(
              'relative z-10 flex w-full max-h-[90vh] flex-col overflow-hidden rounded-[32px] bg-white shadow-2xl',
              animateWidth == null && SIZE_CLASSES[size],
              panelClassName,
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <motion.div layout className="flex min-h-0 flex-col" transition={{ layout: MODAL_LAYOUT_SPRING }}>
              {children}
            </motion.div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
